import React, { useEffect, useState } from "react";
import Layout from "../../components/layout";
import { FaArrowLeft } from "react-icons/fa";
import { Controller } from "react-hook-form";
import CreatableSelect from "react-select/creatable";
import Select from "react-select";

import styles from "./NewSamplePage.module.scss";
import { useNavigate } from "react-router-dom";
import { route } from "../../static/route";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  fetchPartiesData,
  fetchProcessesData,
  fetchItemsData,
  getParties,
  getItems,
  getProcesses,
} from "../../features/newSample/newSampleSlice";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { IFormInput, SamplePartyProps } from "../../types/sample";
import { POST } from "../../services/axios.service";
import { OptionsProps } from "../../types/sample";
import { selectStyles } from "../../static/styles";

const NewSamplePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const parties = useAppSelector(getParties);
  const items = useAppSelector(getItems);
  const processes = useAppSelector(getProcesses);

  const methods = useForm<IFormInput>();

  const {
    handleSubmit,
    reset,
    setValue,
    register,
    control,
    formState: { errors },
  } = methods;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [partiesOption, setPartiesOption] = useState<OptionsProps[]>([]);
  const [itemOption, setItemOption] = useState<OptionsProps[]>([]);
  const [processOption, setProcessOption] = useState<OptionsProps[]>([]);
  const [unitOption] = useState<OptionsProps[]>([
    {
      value: 1,
      label: "વજન - KG",
    },
    {
      value: 2,
      label: "નંગ - Piece",
    },
    {
      value: 3,
      label: "જોડી - Pair",
    },
  ]);

  const handleGoToBack = () => {
    navigate(route.dashboard);
  };

  const onSubmit: SubmitHandler<IFormInput> = async (data) => {
    setIsLoading(true);
    setRequestError(null);

    try {
      if (data) {
        await POST("/v1/worker/samples", {
          party_name: data.party_name,
          item_name: data.item_name,
          process_name: data.process_name,
          qty: data.qty,
          unit: data.unit,
          description: data.description,
        })
          .then(async (response: any) => {
            if (response.data.status === "success") {
              const downloadLink = response.data.data.download_link;
              try {
                const newWindow = window.open(downloadLink, "_blank");
                if (
                  !newWindow ||
                  newWindow.closed ||
                  typeof newWindow.closed == "undefined"
                ) {
                  throw new Error("પોપ્અપ બ્લોક કરેલ છે.");
                }
              } catch (e) {
                window.location.href = downloadLink;
              }
              await reset();
            } else {
              setRequestError(response.data.message);
            }
          })
          .catch((error: any) => {
            setRequestError(error.data.message);
          });
      } else {
        setRequestError("કૃપા કરીને ઓછામાં ઓછી એક આઈટમ ઉમેરો.");
      }
    } catch (error) {
      setRequestError("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.");
    }

    setIsLoading(false);
  };

  useEffect(() => {
    setPartiesOption(
      parties?.map((party: SamplePartyProps) => ({
        value: party.id,
        label:
          party.firm_name +
          (party.name ? " - " + party.name : "") +
          (party.phone ? " (" + party.phone + ")" : ""),
      })) || []
    );
  }, [parties]);

  useEffect(() => {
    setItemOption(
      items?.map((item) => ({
        label: item.name,
        value: item.id,
      })) || []
    );
  }, [items]);

  useEffect(() => {
    setProcessOption(
      processes?.map((process) => ({
        label: process.name,
        value: process.id,
      })) || []
    );
  }, [processes]);

  useEffect(() => {
    dispatch(fetchPartiesData());
    dispatch(fetchItemsData());
    dispatch(fetchProcessesData());
  }, []);

  return (
    <Layout>
      <div className={styles.NewSamplePage}>
        <div className="w-full px-10 h-[200px] text-center flex flex-col justify-center relative">
          <FaArrowLeft
            onClick={handleGoToBack}
            className="absolute top-5 left-5 text-3xl text-white border-2 border-white p-1 rounded-full"
          />

          <h1 className="text-4xl mt-6 font-bold text-white">સેમ્પલ ઉમેરો</h1>
        </div>

        <div className="w-full relative mt-[70px] px-10 py-5 lg:mt-[20px]">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="w-full flex flex-col justify-center">
                <div className="mt-6">
                  <label className="text-primary-1 font-bold">પાર્ટી :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <Controller
                      {...register("party_name", { required: true })}
                      control={control}
                      render={({ field }) => (
                        <CreatableSelect
                          {...field}
                          value={partiesOption.find(
                            (val) => val.label === field?.value
                          )}
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"પાર્ટી નું નામ સિલેક્ટ કરો"}
                          options={partiesOption}
                          onChange={(val: any) =>
                            setValue("party_name", val?.label)
                          }
                          onCreateOption={(inputValue: string) => {
                            const newOption = {
                              label: inputValue,
                              value: 0,
                            };
                            setPartiesOption((prevOptions) => [
                              ...prevOptions,
                              newOption,
                            ]);
                            setValue("party_name", inputValue);
                          }}
                        />
                      )}
                    />
                  </div>
                  {errors.party_name && (
                    <span className="text-red-500">
                      પાર્ટી નું નામ સિલેક્ટ કરો
                    </span>
                  )}
                </div>

                <div className="mt-6">
                  <label className="text-primary-1 font-bold">આઈટમ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <Controller
                      {...register("item_name", {
                        required: true,
                      })}
                      control={control}
                      render={({ field }) => (
                        <CreatableSelect
                          {...field}
                          value={
                            itemOption.find(
                              (val) => val.label === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"આઈટમ નું નામ સિલેક્ટ કરો"}
                          options={itemOption}
                          onChange={(val: any) =>
                            setValue("item_name", val?.label)
                          }
                          onCreateOption={(inputValue: string) => {
                            const newOption = {
                              label: inputValue,
                              value: 0,
                            };
                            setItemOption((prevOptions) => [
                              ...prevOptions,
                              newOption,
                            ]);
                            setValue("item_name", inputValue);
                          }}
                        />
                      )}
                    />
                  </div>
                  {errors.item_name && (
                    <span className="text-red-500">
                      આઈટમ નું નામ સિલેક્ટ કરો
                    </span>
                  )}
                </div>

                <div className="mt-6">
                  <label className="text-primary-1 font-bold">પ્રોસેસ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <Controller
                      {...register("process_name", {
                        required: true,
                      })}
                      control={control}
                      render={({ field }) => (
                        <CreatableSelect
                          {...field}
                          value={
                            processOption.find(
                              (val) => val.label === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"પ્રોસેસ નું નામ સિલેક્ટ કરો"}
                          options={processOption}
                          onChange={(val: any) =>
                            setValue("process_name", val?.label)
                          }
                          onCreateOption={(inputValue: string) => {
                            const newOption = {
                              label: inputValue,
                              value: 0,
                            };
                            setProcessOption((prevOptions) => [
                              ...prevOptions,
                              newOption,
                            ]);
                            setValue("process_name", inputValue);
                          }}
                        />
                      )}
                    />
                  </div>
                  {errors.process_name && (
                    <span className="text-red-500">
                      પ્રોસેસ નું નામ સિલેક્ટ કરો
                    </span>
                  )}
                </div>

                <div className="mt-6">
                  <label className="text-primary-1 font-bold">Qty :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <input
                      className="text-primary-1 px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      type="number"
                      step="any"
                      pattern="[0-9]+(\.[0-9]+)?"
                      placeholder="Qty દાખલ કરો"
                      {...register(`qty`, {
                        required: true,
                      })}
                      onFocus={(e) =>
                        e.target.addEventListener(
                          "wheel",
                          function (e) {
                            e.preventDefault();
                          },
                          { passive: false }
                        )
                      }
                    />
                  </div>
                  {errors.qty && (
                    <span className="text-red-500">Qty દાખલ કરો</span>
                  )}
                </div>

                <div className="mt-6">
                  <label className="text-primary-1 font-bold">યુનિટ :</label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-16 w-full relative rounded">
                    <Controller
                      {...register("unit", {
                        required: true,
                      })}
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          value={
                            unitOption.find(
                              (val) => val.value === field.value
                            ) || null
                          }
                          className="text-primary-1 p-0 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                          styles={selectStyles}
                          placeholder={"યુનિટ સિલેક્ટ કરો"}
                          options={unitOption}
                          onChange={(val: any) => setValue("unit", val?.value)}
                        />
                      )}
                    />
                  </div>
                  {errors.unit && (
                    <span className="text-red-500">યુનિટ સિલેક્ટ કરો</span>
                  )}
                </div>

                <div className="mt-6 hidden">
                  <label className="text-primary-1 font-bold">
                    ડિસ્ક્રિપ્શન :
                  </label>
                  <div className="bg-gradient-to-r from-primary-1 to-primary-2 h-28 w-full relative rounded">
                    <textarea
                      rows={5}
                      className="text-primary-1 focus:outline-none px-3 py-1 font-semibold text-xl placeholder:text-primary-2 absolute top-[2px] left-[2px] w-[calc(100%-4px)] h-[calc(100%-4px)] rounded"
                      placeholder="ડિસ્ક્રિપ્શન દાખલ કરો"
                      {...register("description", { required: false })}
                    />
                  </div>
                  {errors.description && (
                    <span className="text-red-500">ડિસ્ક્રિપ્શન દાખલ કરો</span>
                  )}
                </div>
              </div>

              {requestError !== null && (
                <span className="text-red-500 text-center mt-8">
                  {requestError}
                </span>
              )}

              <div className="my-6 flex justify-center">
                <button
                  className=" mx-auto mt-8 shadow rounded-3xl text-3xl text-white bg-gradient-to-r from-primary-1 to-primary-2 font-bold px-10 py-2"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? "રાહ જુઓ..." : "પુરુ કરો"}
                </button>
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
    </Layout>
  );
};
export default NewSamplePage;
